/**
 * Buying and wearing.
 *
 * Everything in the shop is cosmetic
 * ([Economy](../../../docs/ECONOMY.md#the-shop)): nothing sold here touches
 * learning, difficulty, rewards or progress, and a child who never opens it is
 * not behind in anything that matters.
 *
 * The rules that are worth having in one place are the ones both sides have to
 * agree on — what a purchase costs, whether it is allowed, and what a loadout
 * may contain. The client uses them to grey a button out; the server uses them
 * to decide. Neither is the only one applying them.
 */
import { DROP_RARITIES } from './chests.js';
/** Where a thing is worn. */
export const SLOTS = ['hat', 'glasses', 'top', 'neck', 'shoes', 'held'];
/**
 * Whether this purchase is allowed.
 *
 * Shared so the shop can grey the button out for the same reason the server
 * would refuse — a child should never be able to tap something that is going to
 * say no.
 */
export function refuse(item, balance) {
    if (item === undefined)
        return 'no-such-item';
    if (item.owned)
        return 'already-owned';
    if (balance < item.price)
        return 'not-enough-acorns';
    return null;
}
/** What a refusal should say to a child, rather than to a developer. */
export const refusalText = {
    // Never a telling-off. A child who taps something twice has not done
    // anything wrong, and neither has one who cannot afford it yet.
    'already-owned': 'You already have this one!',
    'not-enough-acorns': 'Not enough acorns yet — keep playing!',
    'no-such-item': 'That one is not in the shop.',
};
/** Is this a rarity a shop item may have? Seasonal is one; droppable ones are the rest. */
export const isDroppable = (rarity) => DROP_RARITIES.includes(rarity);
/** The same generator the chests and eggs use, for the same reason. */
function generator(seed) {
    let hash = 2166136261;
    for (let at = 0; at < seed.length; at++) {
        hash ^= seed.charCodeAt(at);
        hash = Math.imul(hash, 16777619);
    }
    let state = hash >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let drawn = state;
        drawn = Math.imul(drawn ^ (drawn >>> 15), drawn | 1);
        drawn ^= drawn + Math.imul(drawn ^ (drawn >>> 7), drawn | 61);
        return ((drawn ^ (drawn >>> 14)) >>> 0) / 4294967296;
    };
}
export function shuffleOutfit(owned, 
/** The colours owned for an item. Its own colour is always on offer as well. */
dyesFor, seed) {
    const random = generator(seed);
    const picked = {};
    for (const slot of SLOTS) {
        // Sorted before drawing, so the outfit depends on the seed rather than on
        // the order a database happened to return rows in.
        const choices = owned
            .filter((item) => item.slot === slot)
            .sort((a, b) => (a.itemId < b.itemId ? -1 : 1));
        const chosen = choices[Math.floor(random() * choices.length)];
        // Nothing owned in this slot. Left alone rather than emptied: a shuffle
        // must never undress a child.
        if (chosen === undefined) {
            picked[slot] = null;
            continue;
        }
        const colours = [null, ...dyesFor(chosen.itemId)];
        const dye = colours[Math.floor(random() * colours.length)] ?? null;
        picked[slot] = { itemId: chosen.itemId, dye };
    }
    return picked;
}
// --- dyes ([D-041](../../docs/DECISIONS.md#d-041), [D-138](../../docs/DECISIONS.md#d-138)) ---
/** The twelve, in the order the palette is drawn. The only colours an item may be. */
export const DYE_IDS = [
    'berry',
    'coral',
    'gold',
    'sand',
    'leaf',
    'mint',
    'sky',
    'ink',
    'grape',
    'rose',
    'slate',
    'cream',
];
export const isDye = (value) => DYE_IDS.includes(value);
/**
 * What a colour costs, for any item.
 *
 * Flat: a common cap and an epic jacket cost the same to recolour
 * ([Economy](../../docs/ECONOMY.md#dyes)).
 */
export const DYE_PRICE_ACORNS = 150;
/**
 * Whether a dye may be worn on an item.
 *
 * The item's own colour (`null`) always may, and costs nothing — going back
 * is never a purchase. Any other colour must have been bought for this item.
 */
export const mayWearDye = (dye, owned) => dye === null || owned.has(dye);
//# sourceMappingURL=shop.js.map